import React, { useState, useEffect, useCallback } from 'react';
import './Latest_tour_images.css';
import ImageLightbox from "../Components/Helping Component/ImageZoom.jsx";
import api from '../Api.js'; // Adjust the import path as needed

const MultiCardSlider = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [galleryMedia, setGalleryMedia] = useState([]);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(3);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  const minSwipeDistance = 50;

  const isVideoFile = (src) => {
    if (!src) return false;
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
    const srcString = typeof src === 'string' ? src.toLowerCase() : '';
    return videoExtensions.some(ext => srcString.includes(ext));
  };

  // Fetch data from API
  useEffect(() => {
    const fetchHappyCustomers = async () => {
      try {
        setLoading(true);
        const response = await api.get("/happy-customers");
        
        if (response.data.status && response.data.data.length > 0) {
          const customerData = response.data.data[0];
          
          // Get first 10 images
          const first10Images = (customerData.images || [])
            .slice(0, 10)
            .map((image, index) => ({
              id: `img-${image.id}`,
              type: 'image',
              src: image.url,
              alt: `Happy customer image ${index + 1}`
            }));
          
          // Get first 5 videos
          const first5Videos = (customerData.videos || [])
            .slice(0, 5)
            .map((video, index) => ({
              id: `vid-${video.id}`,
              type: 'video',
              src: video.url,
              alt: `Happy customer video ${index + 1}`,
              poster: first10Images[index % first10Images.length]?.src || ''
            }));
          
          // Combine images and videos
          const combinedSlides = [...first10Images, ...first5Videos];
          setSlides(combinedSlides);
        } else {
          setError("No data available");
        }
      } catch (err) {
        console.error("Error fetching happy customers:", err);
        setError("Failed to load gallery");
      } finally {
        setLoading(false);
      }
    };
    
    fetchHappyCustomers();
  }, []);

  useEffect(() => {
    const updateCardsPerView = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setCardsPerView(1);
      } else if (width < 1024) {
        setCardsPerView(2);
      } else {
        setCardsPerView(3);
      }
    };

    updateCardsPerView();
    window.addEventListener('resize', updateCardsPerView);
    return () => window.removeEventListener('resize', updateCardsPerView);
  }, []);

  const totalSlides = slides.length;
  const maxIndex = Math.max(0, totalSlides - cardsPerView);

  const goToPrevious = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => Math.max(0, prev - 1));
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning]);

  const goToNext = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => Math.min(maxIndex, prev + 1));
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning, maxIndex]);

  const goToSlide = (index) => {
    if (isTransitioning || index === currentIndex) return;
    setIsTransitioning(true);
    setCurrentIndex(Math.min(maxIndex, Math.max(0, index)));
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const openLightbox = (index) => {
    const allMedia = slides.map(slide => ({
      url: slide.src,
      type: slide.type || (isVideoFile(slide.src) ? 'video' : 'image'),
      alt: slide.alt
    }));
    setGalleryMedia(allMedia);
    setCurrentMediaIndex(index);
    setLightboxOpen(true);
  };

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      goToNext();
    }
    if (isRightSwipe) {
      goToPrevious();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPrevious, goToNext]);

  useEffect(() => {
    if (slides.length === 0) return;
    
    const interval = setInterval(() => {
      if (currentIndex < maxIndex) {
        goToNext();
      } else {
        setCurrentIndex(0);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [currentIndex, maxIndex, goToNext, slides.length]);

  const cardWidth = 100 / cardsPerView;

  // Skeleton Loader Component
  const SkeletonLoader = () => {
    const skeletonCards = [];
    const skeletonCount = cardsPerView;
    
    for (let i = 0; i < skeletonCount; i++) {
      skeletonCards.push(
        <div 
          key={`skeleton-${i}`} 
          className="mcs-slider-card skeleton-card"
          style={{ width: `${cardWidth}%` }}
        >
          <div className="mcs-card-inner">
            <div className="mcs-card-media-wrapper skeleton-wrapper">
              <div className="skeleton-image"></div>
              <div className="skeleton-overlay">
                <div className="skeleton-number"></div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <>
        <div className="mcs-multi-card-slider" data-testid="multi-card-slider-skeleton">
          <div className="mcs-slider-header">
            <h2 className="mcs-slider-title skeleton-title"></h2>
            <div className="mcs-slider-controls">
              <div className="skeleton-btn skeleton-prev"></div>
              <div className="skeleton-btn skeleton-next"></div>
            </div>
          </div>
          
          <div className="mcs-slider-track-container">
            <div className="mcs-slider-track skeleton-track">
              {skeletonCards}
            </div>
          </div>
          
          <div className="mcs-slider-dots skeleton-dots">
            <div className="skeleton-dot"></div>
            <div className="skeleton-dot"></div>
            <div className="skeleton-dot"></div>
          </div>
        </div>
      </>
    );
  };

  const renderMedia = (slide, idx) => {
    const isVideo = slide.type === 'video' || isVideoFile(slide.src);
    const isHovered = hoveredCard === idx;

    if (isVideo) {
      return (
        <div className="mcs-video-wrapper">
          <video 
            className="mcs-card-video"
            poster={slide.poster}
            preload="metadata"
            muted
            loop
            playsInline
            onMouseEnter={(e) => {
              setHoveredCard(idx);
              if (e.target.play) e.target.play();
            }}
            onMouseLeave={(e) => {
              setHoveredCard(null);
              if (e.target.pause) {
                e.target.pause();
                e.target.currentTime = 0;
              }
            }}
          >
            <source src={slide.src} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="mcs-video-overlay">
            <div className="mcs-play-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </div>
          </div>
          {!isHovered && (
            <div className="mcs-video-badge">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
              <span>Video</span>
            </div>
          )}
        </div>
      );
    }

    return (
      <img 
        src={slide.src} 
        alt={slide.alt}
        className="mcs-card-image"
        loading="lazy"
      />
    );
  };

  // Show skeleton loader while loading
  if (loading) {
    return <SkeletonLoader />;
  }

  if (error) {
    return (
      <div className="mcs-multi-card-slider">
        <div className="mcs-slider-header">
          <h2 className="mcs-slider-title">Latest Tour Gallery</h2>
        </div>
        <div className="error-container">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (slides.length === 0) {
    return (
      <div className="mcs-multi-card-slider">
        <div className="mcs-slider-header">
          <h2 className="mcs-slider-title">Latest Tour Gallery</h2>
        </div>
        <div className="empty-container">
          <p>No media available</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mcs-multi-card-slider" data-testid="multi-card-slider">
        <div className="mcs-slider-header">
          <h2 className="mcs-slider-title">Latest Tour Gallery</h2>
          <div className="mcs-slider-controls">
            <button 
              className="mcs-slider-nav-btn mcs-prev-btn" 
              onClick={goToPrevious}
              disabled={currentIndex === 0 || isTransitioning}
              aria-label="Previous slides"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
            <button 
              className="mcs-slider-nav-btn mcs-next-btn" 
              onClick={goToNext}
              disabled={currentIndex >= maxIndex || isTransitioning}
              aria-label="Next slides"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
          </div>
        </div>

        <div 
          className="mcs-slider-track-container"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div 
            className="mcs-slider-track"
            style={{
              transform: `translateX(-${currentIndex * cardWidth}%)`,
              transition: isTransitioning ? 'transform 0.5s cubic-bezier(0.2, 0.9, 0.4, 1.1)' : 'none'
            }}
          >
            {slides.map((slide, idx) => (
              <div 
                key={slide.id} 
                className="mcs-slider-card"
                style={{ width: `${cardWidth}%` }}
                onClick={() => openLightbox(idx)}
              >
                <div className="mcs-card-inner">
                  <div className="mcs-card-media-wrapper">
                    {renderMedia(slide, idx)}
                    <div className="mcs-card-overlay">
                      <span className="mcs-card-number">{String(idx + 1).padStart(2, '0')}</span>
                      {slide.type === 'video' && (
                        <span className="mcs-media-type-badge">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {maxIndex > 0 && (
          <div className="mcs-slider-dots">
            {Array.from({ length: maxIndex + 1 }).map((_, idx) => (
              <button
                key={idx}
                className={`mcs-dot ${currentIndex === idx ? 'mcs-dot-active' : ''}`}
                onClick={() => goToSlide(idx)}
                aria-label={`Go to slide group ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      <ImageLightbox
        open={lightboxOpen}
        setOpen={setLightboxOpen}
        images={galleryMedia.map(media => media.url)}
        currentIndex={currentMediaIndex}
        mediaTypes={galleryMedia.map(media => media.type)}
      />
    </>
  );
};

export default MultiCardSlider;